import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getWeek,
  getIncomes,
  getIncomesGrouped,
  getExpenses,
  getSalaries,
  getManagers,
  getCategories,
  createIncome,
  createExpense,
  deleteIncome,
  deleteExpense,
  updateIncome,
  getPayments,
  updatePaymentStatus,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  ArrowLeft,
  Plus,
  Calendar as CalendarIcon,
  TrendingUp,
  TrendingDown,
  Wallet,
  Users,
  Trash2,
  Loader2,
  Banknote,
  UsersRound,
  Crown,
  Pencil,
  Check,
  X,
  Ban,
  CreditCard,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export default function WeekDetailsPage() {
  const { weekId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isTeamLeader } = useAuth();

  const [week, setWeek] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [incomesGrouped, setIncomesGrouped] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [salaries, setSalaries] = useState({ by_manager: [], by_team: [] });
  const [managers, setManagers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('incomes');
  
  // Payments (admin only)
  const [payments, setPayments] = useState(null);
  const [updatingPayment, setUpdatingPayment] = useState(null);

  // Income dialog
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [incomeAmount, setIncomeAmount] = useState('');
  const [incomeManager, setIncomeManager] = useState('');
  const [incomeDate, setIncomeDate] = useState(new Date());
  const [incomeComment, setIncomeComment] = useState('');
  const [savingIncome, setSavingIncome] = useState(false);

  // Expense dialog
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date());
  const [expenseComment, setExpenseComment] = useState('');
  const [savingExpense, setSavingExpense] = useState(false);

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null });

  // Edit income dialog
  const [editIncomeDialogOpen, setEditIncomeDialogOpen] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [editIncomeAmount, setEditIncomeAmount] = useState('');
  const [editIncomeManager, setEditIncomeManager] = useState('');
  const [editIncomeDate, setEditIncomeDate] = useState(new Date());
  const [editIncomeComment, setEditIncomeComment] = useState('');
  const [savingEditIncome, setSavingEditIncome] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [weekData, incomesData, managersData, categoriesData] =
        await Promise.all([
          getWeek(weekId),
          getIncomes(weekId),
          getManagers(),
          getCategories(),
        ]);
      setWeek(weekData);
      setIncomes(incomesData);
      setManagers(managersData);
      setCategories(categoriesData);

      // For team leader: also get grouped incomes
      if (isTeamLeader && !isAdmin) {
        const groupedData = await getIncomesGrouped(weekId);
        setIncomesGrouped(groupedData);
      }

      // Get expenses only for admin
      if (isAdmin) {
        const expensesData = await getExpenses(weekId);
        setExpenses(expensesData);
        
        // Get payments for admin
        const paymentsData = await getPayments(weekId);
        setPayments(paymentsData);
      }

      // Get salaries
      const salariesData = await getSalaries(weekId);
      setSalaries(salariesData);

      // Set default manager for manager user
      if (user?.manager_id && managersData.length > 0) {
        setIncomeManager(user.manager_id);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [weekId, isAdmin, user?.manager_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateIncome = async () => {
    if (!incomeAmount || !incomeManager) return;

    try {
      setSavingIncome(true);
      await createIncome({
        week_id: weekId,
        manager_id: incomeManager,
        amount: parseFloat(incomeAmount),
        date: format(incomeDate, 'yyyy-MM-dd'),
        comment: incomeComment || null,
      });
      toast.success('Доход добавлен');
      setIncomeDialogOpen(false);
      resetIncomeForm();
      fetchData();
    } catch (err) {
      console.error('Failed to create income:', err);
      toast.error(err.response?.data?.detail || 'Ошибка добавления дохода');
    } finally {
      setSavingIncome(false);
    }
  };

  const handleCreateExpense = async () => {
    if (!expenseAmount || !expenseCategory) return;

    try {
      setSavingExpense(true);
      await createExpense({
        week_id: weekId,
        category_id: expenseCategory,
        amount: parseFloat(expenseAmount),
        date: format(expenseDate, 'yyyy-MM-dd'),
        comment: expenseComment || null,
      });
      toast.success('Расход добавлен');
      setExpenseDialogOpen(false);
      resetExpenseForm();
      fetchData();
    } catch (err) {
      console.error('Failed to create expense:', err);
      toast.error('Ошибка добавления расхода');
    } finally {
      setSavingExpense(false);
    }
  };

  const handleDeleteIncome = async (incomeId) => {
    setDeleteDialog({ open: true, type: 'income', id: incomeId });
  };

  const handleDeleteExpense = async (expenseId) => {
    setDeleteDialog({ open: true, type: 'expense', id: expenseId });
  };

  const confirmDelete = async () => {
    const { type, id } = deleteDialog;
    setDeleteDialog({ open: false, type: null, id: null });
    try {
      if (type === 'income') {
        await deleteIncome(id);
        toast.success('Доход удалён');
      } else {
        await deleteExpense(id);
        toast.success('Расход удалён');
      }
      fetchData();
    } catch (err) {
      console.error(`Failed to delete ${type}:`, err);
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const resetIncomeForm = () => {
    setIncomeAmount('');
    if (!user?.manager_id) {
      setIncomeManager('');
    }
    setIncomeDate(new Date());
    setIncomeComment('');
  };

  const handlePaymentStatusChange = async (managerId, newStatus) => {
    try {
      setUpdatingPayment(managerId);
      await updatePaymentStatus(managerId, weekId, newStatus);
      // Refresh payments
      const paymentsData = await getPayments(weekId);
      setPayments(paymentsData);
      toast.success('Статус выплаты обновлён');
    } catch (err) {
      console.error('Failed to update payment:', err);
      toast.error('Ошибка обновления статуса');
    } finally {
      setUpdatingPayment(null);
    }
  };

  const handleEditIncome = (income) => {
    setEditingIncome(income);
    setEditIncomeAmount(income.amount.toString());
    setEditIncomeManager(income.manager_id);
    setEditIncomeDate(parseISO(income.date));
    setEditIncomeComment(income.comment || '');
    setEditIncomeDialogOpen(true);
  };

  const handleUpdateIncome = async () => {
    if (!editIncomeAmount || !editIncomeManager || !editingIncome) return;

    try {
      setSavingEditIncome(true);
      await updateIncome(editingIncome.id, {
        week_id: weekId,
        manager_id: editIncomeManager,
        amount: parseFloat(editIncomeAmount),
        date: format(editIncomeDate, 'yyyy-MM-dd'),
        comment: editIncomeComment || null,
      });
      toast.success('Доход обновлён');
      setEditIncomeDialogOpen(false);
      setEditingIncome(null);
      fetchData();
    } catch (err) {
      console.error('Failed to update income:', err);
      toast.error(err.response?.data?.detail || 'Ошибка обновления дохода');
    } finally {
      setSavingEditIncome(false);
    }
  };

  const resetExpenseForm = () => {
    setExpenseAmount('');
    setExpenseCategory('');
    setExpenseDate(new Date());
    setExpenseComment('');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value) + ' $';
  };

  const formatDate = (dateStr) => {
    return format(parseISO(dateStr), 'd MMM yyyy', { locale: ru });
  };

  // Only admin can add/edit/delete incomes and expenses
  const canEdit = isAdmin;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!week) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Неделя не найдена</p>
        <Button variant="link" onClick={() => navigate('/weeks')}>
          Вернуться к неделям
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/weeks')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {format(parseISO(week.start_date), 'd MMM', { locale: ru })} -{' '}
            {format(parseISO(week.end_date), 'd MMM yyyy', { locale: ru })}
          </h1>
          <span
            className={cn(
              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
              week.status === 'open' ? 'status-open' : 'status-closed'
            )}
          >
            {week.status === 'open' ? 'Открыта' : 'Закрыта'}
          </span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
              <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Доход</p>
              <p className="text-lg font-semibold income-text">{formatCurrency(week.total_income)}</p>
            </div>
          </div>
        </Card>

        {isAdmin && (
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <TrendingDown className="h-5 w-5 text-red-500 dark:text-red-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Расход</p>
                <p className="text-lg font-semibold expense-text">{formatCurrency(week.total_expense)}</p>
              </div>
            </div>
          </Card>
        )}

        <Card className="stat-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <Banknote className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Зарплаты</p>
              <p className="text-lg font-semibold">{formatCurrency(week.total_salaries)}</p>
            </div>
          </div>
        </Card>

        {isAdmin && (
          <Card className="stat-card">
            <div className="flex items-center gap-3">
              <div className={cn(
                'p-2 rounded-lg',
                week.net_profit >= 0 
                  ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                  : 'bg-red-100 dark:bg-red-900/30'
              )}>
                <Wallet className={cn(
                  'h-5 w-5',
                  week.net_profit >= 0 
                    ? 'text-emerald-600 dark:text-emerald-400' 
                    : 'text-red-500 dark:text-red-400'
                )} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Прибыль</p>
                <p className={cn(
                  'text-lg font-semibold',
                  week.net_profit >= 0 ? 'profit-positive' : 'profit-negative'
                )}>
                  {week.net_profit >= 0 ? '+' : ''}{formatCurrency(week.net_profit)}
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Tabs - 3 tabs for admin, 2 tabs for manager */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <TabsList>
            <TabsTrigger value="incomes" data-testid="incomes-tab">
              Доходы ({isTeamLeader && !isAdmin && incomesGrouped 
                ? incomesGrouped.by_team?.reduce((acc, t) => acc + t.managers.length, 0) || 0
                : incomes.length})
            </TabsTrigger>
            {isAdmin ? (
              <TabsTrigger value="expenses" data-testid="expenses-tab">
                Расходы ({expenses.length})
              </TabsTrigger>
            ) : null}
            <TabsTrigger value="salaries" data-testid="salaries-tab">
              Зарплаты ({(salaries.by_team?.length || 0) + (salaries.by_manager?.length || 0)})
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="payments" data-testid="payments-tab">
                <CreditCard className="h-4 w-4 mr-1" />
                Выплаты
              </TabsTrigger>
            )}
          </TabsList>

          {canEdit && activeTab === 'incomes' && (
            <Button
              size="sm"
              onClick={() => setIncomeDialogOpen(true)}
              data-testid="add-income-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Добавить
            </Button>
          )}
          {canEdit && activeTab === 'expenses' && isAdmin && (
            <Button
              size="sm"
              onClick={() => setExpenseDialogOpen(true)}
              data-testid="add-expense-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Добавить
            </Button>
          )}
        </div>

        {/* Incomes Tab */}
        <TabsContent value="incomes" className="mt-4">
          {/* Team Leader sees grouped incomes by manager */}
          {isTeamLeader && !isAdmin && incomesGrouped ? (
            incomesGrouped.by_team?.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <TrendingUp className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Нет доходов</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {incomesGrouped.by_team.map((teamData) => (
                  <Card key={teamData.team_id || 'no-team'} className="animate-fade-in">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-primary" />
                          {teamData.team_name}
                        </span>
                        <span className="font-bold income-text">
                          {formatCurrency(teamData.total_team_income)}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="divide-y">
                        {teamData.managers.map((m) => (
                          <div
                            key={m.manager_id}
                            className="flex items-center justify-between py-2"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{m.manager_name}</span>
                              <span className="text-xs text-muted-foreground">
                                ({m.deposits_count} {m.deposits_count === 1 ? 'депозит' : 'депозитов'})
                              </span>
                            </div>
                            <span className="font-semibold income-text">
                              +{formatCurrency(m.total_income)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          ) : (
            /* Admin and Manager see individual incomes */
            incomes.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <TrendingUp className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Нет доходов</p>
                  {canEdit && (
                    <Button
                      variant="link"
                      onClick={() => setIncomeDialogOpen(true)}
                      className="mt-2"
                    >
                      Добавить первый доход
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {incomes.map((income) => (
                  <Card key={income.id} className="animate-fade-in" data-testid={`income-${income.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-semibold income-text">
                              +{formatCurrency(income.amount)}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {income.manager_name} • {formatDate(income.date)}
                          </p>
                          {income.comment && (
                            <p className="text-sm mt-2">{income.comment}</p>
                          )}
                        </div>
                        {canEdit && (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-muted-foreground hover:text-primary"
                              onClick={() => handleEditIncome(income)}
                              data-testid={`edit-income-${income.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDeleteIncome(income.id)}
                              data-testid={`delete-income-${income.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          )}
        </TabsContent>

        {/* Expenses Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="expenses" className="mt-4">
            {expenses.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <TrendingDown className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Нет расходов</p>
                  {canEdit && (
                    <Button
                      variant="link"
                      onClick={() => setExpenseDialogOpen(true)}
                      className="mt-2"
                    >
                      Добавить первый расход
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {expenses.map((expense) => (
                  <Card key={expense.id} className="animate-fade-in" data-testid={`expense-${expense.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <span className="text-lg font-semibold expense-text">
                            -{formatCurrency(expense.amount)}
                          </span>
                          <p className="text-sm text-muted-foreground mt-1">
                            {expense.category_name} • {formatDate(expense.date)}
                          </p>
                          {expense.comment && (
                            <p className="text-sm mt-2">{expense.comment}</p>
                          )}
                        </div>
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteExpense(expense.id)}
                            data-testid={`delete-expense-${expense.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* Salaries Tab */}
        <TabsContent value="salaries" className="mt-4">
          {(!salaries.by_manager || salaries.by_manager.length === 0) && (!salaries.by_team || salaries.by_team.length === 0) ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Banknote className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Нет зарплат</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Добавьте доходы, чтобы рассчитать зарплаты
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Group by team */}
              {salaries.by_team && salaries.by_team.length > 0 ? (
                salaries.by_team.map((teamData, teamIdx) => (
                  <div key={teamIdx} className="space-y-3">
                    {/* Team header with leader salary */}
                    <div className="flex items-center justify-between p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <div className="flex items-center gap-3">
                        <UsersRound className="h-5 w-5 text-primary" />
                        <div>
                          <h3 className="font-semibold">
                            {isTeamLeader && !teamData.team_id ? 'Моя зарплата' : teamData.team_name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {isTeamLeader && !teamData.team_id 
                              ? `Залив: ${formatCurrency(teamData.total_team_income)}`
                              : `Общий залив: ${formatCurrency(teamData.total_team_income)}`
                            }
                          </p>
                        </div>
                      </div>
                      {(teamData.leader_name || teamData.leader_salary > 0) && (
                        <div className="text-right">
                          <div className="flex items-center gap-2 text-amber-600">
                            <Crown className="h-4 w-4" />
                            <span className="text-sm">{teamData.leader_name || 'Тимлид'}</span>
                          </div>
                          <p className="text-lg font-semibold text-amber-600">
                            {formatCurrency(teamData.leader_salary)}
                          </p>
                          <p className="text-xs text-muted-foreground">{teamData.leader_percent}% тимлиду</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Managers in team */}
                    {teamData.managers.map((salary, idx) => (
                      <Card key={idx} className="animate-fade-in ml-4" data-testid={`salary-${teamIdx}-${idx}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Users className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-medium">{salary.manager_name}</h3>
                              <p className="text-sm text-muted-foreground">
                                Залив: {formatCurrency(salary.total_income)} • {salary.percent_applied}%
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-semibold text-primary">
                                {formatCurrency(salary.salary)}
                              </p>
                              <p className="text-xs text-muted-foreground">зарплата</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ))
              ) : (
                // Fallback to flat list if no teams
                salaries.by_manager.map((salary, idx) => (
                  <Card key={idx} className="animate-fade-in" data-testid={`salary-${idx}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium">{salary.manager_name}</h3>
                          <p className="text-sm text-muted-foreground">
                            Залив: {formatCurrency(salary.total_income)} • {salary.percent_applied}%
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-primary">
                            {formatCurrency(salary.salary)}
                          </p>
                          <p className="text-xs text-muted-foreground">зарплата</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </TabsContent>

        {/* Payments Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="payments" className="mt-4">
            {!payments ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Team Leaders */}
                {payments.team_leaders?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Crown className="h-4 w-4 text-amber-500" />
                        Тимлиды
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {payments.team_leaders.map((tl) => (
                          <div
                            key={tl.person_id}
                            className={cn(
                              "flex items-center justify-between px-4 py-2 text-sm",
                              tl.status === 'paid' && "bg-green-500/5",
                              tl.status === 'postponed' && "bg-yellow-500/5"
                            )}
                          >
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              <span className="font-medium truncate w-32">{tl.person_name}</span>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {tl.teams_income?.map((t, idx) => (
                                  <span key={idx} className="bg-muted px-2 py-0.5 rounded">
                                    {t.team_name}: {Math.round(t.commission || 0).toLocaleString()} $
                                  </span>
                                ))}
                                {tl.personal_income > 0 && (
                                  <span className="bg-muted px-2 py-0.5 rounded">
                                    Личный: {Math.round(tl.personal_salary || 0).toLocaleString()} $
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-6">
                              <span className="font-bold text-base w-24 text-right">
                                {Math.round(tl.total_salary || 0).toLocaleString()} $
                              </span>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handlePaymentStatusChange(tl.person_id, 'paid')}
                                  disabled={updatingPayment === tl.person_id}
                                  className={cn(
                                    "p-1.5 rounded-full transition-colors",
                                    tl.status === 'paid' 
                                      ? "bg-green-500 text-white" 
                                      : "bg-muted hover:bg-green-500/20 text-muted-foreground"
                                  )}
                                  title="Выплачено"
                                >
                                  <Check className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => handlePaymentStatusChange(tl.person_id, 'not_paid')}
                                  disabled={updatingPayment === tl.person_id}
                                  className={cn(
                                    "p-1.5 rounded-full transition-colors",
                                    tl.status === 'not_paid' 
                                      ? "bg-red-500 text-white" 
                                      : "bg-muted hover:bg-red-500/20 text-muted-foreground"
                                  )}
                                  title="Не выплачено"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => handlePaymentStatusChange(tl.person_id, 'postponed')}
                                  disabled={updatingPayment === tl.person_id}
                                  className={cn(
                                    "p-1.5 rounded-full transition-colors",
                                    tl.status === 'postponed' 
                                      ? "bg-yellow-500 text-white" 
                                      : "bg-muted hover:bg-yellow-500/20 text-muted-foreground"
                                  )}
                                  title="Перенесено"
                                >
                                  <Ban className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Managers by Team */}
                {payments.managers_by_team && Object.entries(payments.managers_by_team).map(([teamId, managers]) => (
                  managers.length > 0 && (
                    <Card key={teamId}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <UsersRound className="h-4 w-4 text-blue-500" />
                          {managers[0].team_name || 'Без команды'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="divide-y">
                          {managers.map((m) => (
                            <div
                              key={m.person_id}
                              className={cn(
                                "flex items-center justify-between px-4 py-2 text-sm",
                                m.status === 'paid' && "bg-green-500/5",
                                m.status === 'postponed' && "bg-yellow-500/5"
                              )}
                            >
                              <div className="flex items-center gap-4 flex-1 min-w-0">
                                <span className="font-medium truncate w-32">{m.person_name}</span>
                                <span className="text-xs text-muted-foreground">
                                  Залив: {Math.round(m.total_income || 0).toLocaleString()} $
                                </span>
                              </div>
                              <div className="flex items-center gap-6">
                                <span className="font-bold text-base w-24 text-right">
                                  {Math.round(m.total_salary || 0).toLocaleString()} $
                                </span>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => handlePaymentStatusChange(m.person_id, 'paid')}
                                    disabled={updatingPayment === m.person_id}
                                    className={cn(
                                      "p-1.5 rounded-full transition-colors",
                                      m.status === 'paid' 
                                        ? "bg-green-500 text-white" 
                                        : "bg-muted hover:bg-green-500/20 text-muted-foreground"
                                    )}
                                    title="Выплачено"
                                  >
                                    <Check className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handlePaymentStatusChange(m.person_id, 'not_paid')}
                                    disabled={updatingPayment === m.person_id}
                                    className={cn(
                                      "p-1.5 rounded-full transition-colors",
                                      m.status === 'not_paid' 
                                        ? "bg-red-500 text-white" 
                                        : "bg-muted hover:bg-red-500/20 text-muted-foreground"
                                    )}
                                    title="Не выплачено"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handlePaymentStatusChange(m.person_id, 'postponed')}
                                    disabled={updatingPayment === m.person_id}
                                    className={cn(
                                      "p-1.5 rounded-full transition-colors",
                                      m.status === 'postponed' 
                                        ? "bg-yellow-500 text-white" 
                                        : "bg-muted hover:bg-yellow-500/20 text-muted-foreground"
                                    )}
                                    title="Перенесено"
                                  >
                                    <Ban className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )
                ))}

                {payments.team_leaders?.length === 0 && 
                 (!payments.managers_by_team || Object.values(payments.managers_by_team).flat().length === 0) && (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Нет данных для выплат на этой неделе</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Income Dialog */}
      <Dialog open={incomeDialogOpen} onOpenChange={setIncomeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Добавить доход</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Сумма</Label>
              <Input
                type="number"
                placeholder="0"
                value={incomeAmount}
                onChange={(e) => setIncomeAmount(e.target.value)}
                data-testid="income-amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Менеджер</Label>
              <Select 
                value={incomeManager} 
                onValueChange={setIncomeManager}
                disabled={!!user?.manager_id}
              >
                <SelectTrigger data-testid="income-manager-select">
                  <SelectValue placeholder="Выберите менеджера" />
                </SelectTrigger>
                <SelectContent>
                  {managers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Дата</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(incomeDate, 'PPP', { locale: ru })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={incomeDate}
                    onSelect={(d) => d && setIncomeDate(d)}
                    locale={ru}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Комментарий (необязательно)</Label>
              <Textarea
                placeholder="Комментарий..."
                value={incomeComment}
                onChange={(e) => setIncomeComment(e.target.value)}
                data-testid="income-comment-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIncomeDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleCreateIncome}
              disabled={!incomeAmount || !incomeManager || savingIncome}
              data-testid="confirm-create-income"
            >
              {savingIncome ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Добавить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense Dialog */}
      <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Добавить расход</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Сумма</Label>
              <Input
                type="number"
                placeholder="0"
                value={expenseAmount}
                onChange={(e) => setExpenseAmount(e.target.value)}
                data-testid="expense-amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Категория</Label>
              <Select value={expenseCategory} onValueChange={setExpenseCategory}>
                <SelectTrigger data-testid="expense-category-select">
                  <SelectValue placeholder="Выберите категорию" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Дата</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(expenseDate, 'PPP', { locale: ru })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={expenseDate}
                    onSelect={(d) => d && setExpenseDate(d)}
                    locale={ru}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Комментарий (необязательно)</Label>
              <Textarea
                placeholder="Комментарий..."
                value={expenseComment}
                onChange={(e) => setExpenseComment(e.target.value)}
                data-testid="expense-comment-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleCreateExpense}
              disabled={!expenseAmount || !expenseCategory || savingExpense}
              data-testid="confirm-create-expense"
            >
              {savingExpense ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Добавить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Income Dialog */}
      <Dialog open={editIncomeDialogOpen} onOpenChange={setEditIncomeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Редактировать доход</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Сумма</Label>
              <Input
                type="number"
                placeholder="0"
                value={editIncomeAmount}
                onChange={(e) => setEditIncomeAmount(e.target.value)}
                data-testid="edit-income-amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Менеджер</Label>
              <Select value={editIncomeManager} onValueChange={setEditIncomeManager}>
                <SelectTrigger data-testid="edit-income-manager-select">
                  <SelectValue placeholder="Выберите менеджера" />
                </SelectTrigger>
                <SelectContent>
                  {managers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Дата</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(editIncomeDate, 'PPP', { locale: ru })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={editIncomeDate}
                    onSelect={(d) => d && setEditIncomeDate(d)}
                    locale={ru}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Комментарий (необязательно)</Label>
              <Textarea
                placeholder="Комментарий..."
                value={editIncomeComment}
                onChange={(e) => setEditIncomeComment(e.target.value)}
                data-testid="edit-income-comment-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditIncomeDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleUpdateIncome}
              disabled={!editIncomeAmount || !editIncomeManager || savingEditIncome}
              data-testid="confirm-edit-income"
            >
              {savingEditIncome ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить {deleteDialog.type === 'income' ? 'доход' : 'расход'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя будет отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
